import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import { DEBUG, PATH } from '../../utils/constants';

import { Milestone } from 'lucide-react';
import styles from "./StartGameVillages.module.scss";
import useSafeAsyncEffect from '../../utils/hooks/useSafeAsyncEffect';
import useQuestionsManager from '../../utils/hooks/useQuestionsManager';
import LoadingIndicator from '../../utils/LoadingIndicator/LoadingIndicator';
import SmartRenderer from '../../utils/SmartRenderer/SmartRenderer';

import Questions from "../Questions/Questions";
import QuestionFooter from "../QuestionFooter/QuestionFooter";

// APIS 
import { getVillage } from '../../utils/api/villagesAPI';
import { getParcours } from '../../utils/api/parcoursAPI';
import { getAllQuestionsByParcours } from '../../utils/api/questionsAPI';
import { getAllRepLibresByQuestion } from '../../utils/api/reponsesLibresAPI';
import { getAllRepQcmByQuestion } from '../../utils/api/reponsesQcmAPI';
import { createProgression, achieveProgression } from '../../utils/api/progressionAPI';
import { sendScore } from '../../utils/api/scoresAPI';

import { useGame } from "../../context/GameContext";
import { useAuth } from "../../context/AuthContext";
import { LanguageContext } from '../../context/LanguageContext';
import translations from '../../utils/translations/StartGameVillagesTranslations';

const debug = DEBUG;

const StartGameVillages = ({villageId, parcoursId}) => {

    const { user } = useAuth();

    // Utilisation du contexte de langue global et accès simplifié aux traductions
    const { language, changeLanguage } = useContext(LanguageContext);
    const t = translations[language] || translations.fr;

    const navigate = useNavigate();
    const { startNewGame, clearGameData } = useGame(); // FAIRE LE POINT SUR SON INTERET AVEC LE NOUVEAU SYSTEME !!!

    const { questions, initializeQuestions, setQuestionAchieved, startQuestionTimer, updateQuestionField, saveQuestionsToStorage, loadFromStorage, clearQuestions } = useQuestionsManager("villages", parcoursId);

// --------------------- STATES --------------------------

    const [village, setVillage] = useState(null);
    const [parcours, setParcours] = useState(null);
    const [repLibres, setRepLibres] = useState([]);
    const [repQcm, setRepQcm] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showIntro, setShowIntro] = useState(true);
    const [isReplay, setIsReplay] = useState(false);
    const [showOutro, setShowOutro] = useState(false);

    const [showAbortedQuestionsModal, setShowAbortedQuestionsModal] = useState(false);

    const [nbPlayers, setNbPlayers] = useState(1);

// --------------------- EFFECTS --------------------------

    useEffect(() => { 
        if (questions && questions.length > 0) {
            
            const hasStarted = questions.some(q => q.startTime !== null); // Si au moins une question a un startTime défini → le joueur a commencé
            if (hasStarted) setShowIntro(false);
            
          }
    }, [questions]);

    useSafeAsyncEffect(async (isMounted) => {

        const fetchData = async () => {
            try {
                setLoading(true);
    
                const villageData = await getVillage(villageId);
                const parcoursData = await getParcours(parcoursId);
                if (!isMounted) return;  // 🚫 Si le composant est démonté entre temps, on stoppe

                setVillage(villageData);
                setParcours(parcoursData);
    
                // ✅ 1. Essayer de charger les questions depuis le localStorage 
                const restored = await loadFromStorage();
                const localQuestions = localStorage.getItem(`questions_villages_${parcoursId}`);
                const localStructure = localStorage.getItem(`questionsStructure_${parcoursId}`);

                if (restored && localStructure) {
                    
                    const parsedQuestions = JSON.parse(localQuestions);
                    if (debug) console.log("➡️ Questions locales trouvées :", parsedQuestions);
                    const parsedStructure = JSON.parse(localStructure);
                    if (debug) console.log("➡️ Structure des questions locales trouvée :", parsedStructure);
    
                    // Récupère les questions réorganisées selon la structure
                    // const reorderedQuestions = parsedStructure.map(({ type, id }) => {
                    //     const found = parsedQuestions.find(q => q.id === id && q.type === type);
                    //     if (!found) {
                    //         if (debug) console.warn(`⚠️ Question id:${id} type:${type} non trouvée dans les données locales`);
                    //     }
                    //     return found;
                    // }).filter(q => q !== undefined);
    
                    // Chargement des réponses liées (réponses libres)
                    const repLibresData = await Promise.all(
                        parsedQuestions
                            .filter(q => q.type === "libre")
                            .map(async q => {
                                const reponse = await getAllRepLibresByQuestion(q.id);
                                return {
                                    id: reponse.id,
                                    possibilities: reponse.possibilities,
                                    idQuestion: q.id
                                };
                            })
                    );
                    if (isMounted) setRepLibres(repLibresData);
    
                    // Chargement des réponses liées (réponses qcm)
                    const repQcmData = await Promise.all(
                        parsedQuestions
                            .filter(q => q.type === "qcm")
                            .map(async q => {
                                const reponses = await getAllRepQcmByQuestion(q.id);
                                return reponses.map(r => ({
                                    id: r.id,
                                    idQuestion: q.id,
                                    txt: r.txt,
                                    img: r.img,
                                    isTrue: r.is_true
                                }));
                            })
                    ).then(arrays => arrays.flat());
                    
                    if (isMounted) setRepQcm(repQcmData);
                // ✅ 2. Sinon on charge depuis l'API
                } else {
                    if (debug) console.log("📡 Aucune donnée locale, récupération depuis l'API...");
    
                    const questionsData = await getAllQuestionsByParcours(parcoursId);

                    if (isMounted) initializeQuestions(questionsData);
    
                    const questionsStructure = questionsData.map(q => ({ type: q.type, id: q.id }));
                    localStorage.setItem(`questionsStructure_${parcoursId}`, JSON.stringify(questionsStructure));
    
                    if (debug) console.log("➡️ Questions API sauvegardées :", questionsData);
                    if (debug) console.log("➡️ Structure API sauvegardée :", questionsStructure);

                    const localRepLibres = localStorage.getItem(`repLibres_villages_${parcoursId}`);
                    const localRepQcm = localStorage.getItem(`repQcm_villages_${parcoursId}`);

                    if (localRepLibres && localRepQcm) {
                    setRepLibres(JSON.parse(localRepLibres));
                    setRepQcm(JSON.parse(localRepQcm));
                    if (debug) console.log("Réponses chargées depuis localStorage !");
                    } else {
    
                        // Chargement des réponses liées (réponses libres)
                        const repLibresData = await Promise.all(
                            questionsData
                                .filter(q => q.type === "libre")
                                .map(async q => {
                                    const reponse = await getAllRepLibresByQuestion(q.id);
                                    return {
                                        id: reponse.id,
                                        possibilities: reponse.possibilities,
                                        idQuestion: q.id
                                    };
                                })
                        );
                        if (isMounted) {
                            setRepLibres(repLibresData);
                            localStorage.setItem(`repLibres_villages_${parcoursId}`, JSON.stringify(repLibresData));
                        }
                        
                        // Chargement des réponses liées (réponses qcm)
                        const repQcmData = await Promise.all(
                            questionsData
                                .filter(q => q.type === "qcm")
                                .map(async q => {
                                    const reponses = await getAllRepQcmByQuestion(q.id);
                                    return reponses.map(r => ({
                                        id: r.id,
                                        idQuestion: q.id,
                                        txt: r.txt,
                                        img: r.img,
                                        isTrue: r.is_true
                                    }));
                                })
                        ).then(arrays => arrays.flat());
                        if (isMounted) {
                            setRepQcm(repQcmData);
                            localStorage.setItem(`repQcm_villages_${parcoursId}`, JSON.stringify(repQcmData));
                        }
                    }
                }
    
            } catch (error) {
                console.error("❌ Erreur lors de la récupération des données :", error);
            } finally {
                if (isMounted) setLoading(false);
            }
        };
    
        fetchData();

        return () => {
            isMounted = false; // Nettoyage si composant démonté
        };
    }, [villageId, parcoursId], { debug: debug });


// --------------------- HANDLERS --------------------------

    const handleStartGame = async () => {
        if (debug) console.log("Démarrage d'un nouveau jeu, parcoursId : ", parcoursId);
        // startNewGame(parcoursId, villageId); //(parcoursId, villageId)
        // setShowIntro(false);

        try {
            const userId = user?.id;
            if (!userId) throw new Error("Utilisateur non connecté");

            const existingProgressionId = localStorage.getItem(`progression_${parcoursId}`);
            if (debug) console.log("Progression enregistrée en localStorage : ", existingProgressionId);
            
            if (!existingProgressionId) {

                const res = await createProgression(userId, parcoursId, nbPlayers);

                if (res?.success) {
                    // Stocker l'id progression en local
                    localStorage.setItem(`progression_${parcoursId}`, res.id);
                    if (debug) console.log("🆕 Progression créée :", res.id);
                } else {
                    console.error("Erreur lors de la création de la progression :", res);
                }
            } else {
                if (debug) console.log("📦 Progression déjà existante :", existingProgressionId);
            }

            startNewGame(parcoursId, villageId); // conserve le GameContext
            setShowIntro(false);
        } catch (error) {
            if (debug) console.error("Erreur dans handleStartGame :", error);
        }
    }

    const handleFinishGame = () => { 
        if(hasAbortedQuestions()) {
            setShowAbortedQuestionsModal(true);
        } else {
            setShowOutro(true);  // 🔥 Active l'écran de fin
        }
    };

    // Fonction de vérification de s'il y a une/des questions qui ont été passées
    const hasAbortedQuestions = () => {
        return questions.some(q => q.startTime && q.aborted);
    };

    const countAbortedQuestions = () => {
        return questions.filter(q => q.startTime && q.aborted).length;
    };

    const reloadQuestions = () => {
        const updatedQuestions = questions.map(q => {
            if (q.aborted) {
                return {
                    ...q,
                    achieved: false,
                    aborted: false
                };
            }
            return q;
        });

        // Met à jour les questions dans le manager (useQuestionsManager)
        initializeQuestions(updatedQuestions);

        // Recharge l’interface de jeu
        setShowAbortedQuestionsModal(false);
        setShowIntro(false);
    }

    const handleEndGame = async () => {    

        const progressionId = localStorage.getItem(`progression_${parcoursId}`);
        const userId = user?.id;
        let isClosed = false;
        // await finishProgression(user.id, parcoursId);

        if (progressionId) {

            for (const q of questions) {
                if (!q.achieved || !q.duration) continue;

                const score = {
                    idUser: userId,
                    idQuestion: q.id,
                    time: q.duration,
                    compass: q.nbCompass,
                    aborted: q.aborted,
                    idProgression: progressionId,
                };

                await sendScore(score);
            }

            // const abortedQuestions = await getAbortedQuestions(progressionId); // Système pour passer les questions
            const abortedQuestions = false;
            if (!abortedQuestions || abortedQuestions.length === 0) {
                const res = await achieveProgression(progressionId, userId, parcoursId);
                if (debug) console.log("Réponse serveur :", res);

                // Tu peux vérifier le succès si ton API renvoie un indicateur
                if (res?.success) {
                    if (debug) console.log("✅ Partie clôturée côté serveur");
                    isClosed = true;
                } else {
                    if (debug) console.log("⚠ Partie non clôturée : échec côté serveur");
                }
            } else {
                if (debug) console.log("Partie incomplète, non clôturée");
            }
        }

        if (isClosed) {
            localStorage.removeItem(`progression_${parcoursId}`);
        } else {
            // Tu peux afficher un message à l'utilisateur ici si tu veux
            // alert(t.errorClosingGame || "La clôture de la partie a échoué, veuillez réessayer.");
            // En attendant...
            localStorage.removeItem(`progression_${parcoursId}`);
        }
        
        // Nettoyer localStorage pour ce parcours
        localStorage.removeItem(`questionsStructure_${parcoursId}`);
        localStorage.removeItem(`repLibres_villages_${parcoursId}`);
        localStorage.removeItem(`repQcm_villages_${parcoursId}`);

        clearQuestions();
        clearGameData(); // Supprime uniquement les données du jeu sans toucher aux tokens d'authentification
        navigate("/"); // Rediriger par la suite vers le compte utilisateur avec les résultats du parcours !!!
    };


// --------------------- RENDERS --------------------------

    // Affichage d'un état de chargement pendant la récupération des données
    if (loading) {
        return <LoadingIndicator />;
    }

    // Vérification si les données existent avant de les afficher
    if (!village || !parcours) {
        return <p>{t.loadingError}</p>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.questionHeader}>
                <div className={styles.iconContainer}><span className={styles.icon} ><Milestone /></span></div>
                <p><strong>{village.nom}</strong> - {parcours.nom}</p>
            </div>
            
            {showIntro ? (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <div className={styles.introText}>
                            {/* <MarkdownRenderer content={parcours.intro} /> */}
                            <div className="rt-content">
                                <SmartRenderer content={parcours.intro} />
                            </div>
                        </div>

                        { !isReplay ? (
                            <>
                                <div className={styles.card}>
                                    <p className={styles.nbPlayersText}>Combien de joueurs participent à ce parcours ?</p>
                                    <input 
                                        type="number"
                                        className={styles.nbPlayersInput}
                                        value={nbPlayers}
                                        onChange={e => setNbPlayers(e.target.value)}
                                        placeholder={t.nbPlayersPlaceholder || "Nombre de joueurs"}
                                    />
                                </div>

                                <div className={styles.buttContainer}>
                                    <button 
                                        className={styles.startButton}
                                        onClick={handleStartGame}
                                    >
                                        {t.startButton}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className={styles.buttContainer}>
                                <button 
                                    className={styles.startButton}
                                    onClick={() => {
                                        setShowIntro(false);
                                        setIsReplay(false);
                                    }}
                                >
                                    Retour au jeu
                                </button>
                            </div>
                        )}
                        
                    </div>
                </div>
            ) : showOutro ? (  // 🔥 Ajout de showOutro ici
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <div className={styles.outroText}>
                            {/* <MarkdownRenderer content={parcours.outro} /> */}
                            <div className="rt-content">
                                <SmartRenderer content={parcours.outro} />
                            </div>
                        </div>
                        <button 
                            className={styles.endButton}
                            onClick={handleEndGame}  // Une fonction pour réinitialiser et rediriger
                        >
                            {t.endButton}
                        </button>
                    </div>
                </div> 
                
            ) : showAbortedQuestionsModal ? (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <p className={styles.outroText}>Vous avez {countAbortedQuestions()} question(s) en attente, que voulez-vous faire ?</p>
                        <button className={styles.endButton} onClick={reloadQuestions}>Reprendre les questions passées</button>
                        <button className={styles.stopButton} onClick={() => {
                            setShowAbortedQuestionsModal(false);
                            setShowOutro(true);
                        }}>Abandonner et terminer le parcours</button>
                    </div>
                </div>
            ) : (
                <Questions 
                    parcoursId={parcoursId} 
                    questions={questions}
                    repLibres={repLibres}
                    repQcm={repQcm}
                    onFinishGame={handleFinishGame}
                    setQuestionAchieved={setQuestionAchieved}
                    startQuestionTimer={startQuestionTimer}
                    saveQuestionsToStorage={saveQuestionsToStorage}
                    updateQuestionField={updateQuestionField}
                />
            )}
            {/* <div className={styles.questionFooter}>
                <p><strong>Besoin d'aide ?</strong> --</p>
            </div> */}
            <QuestionFooter type={"villages"} gId={parcoursId} setShowIntro={setShowIntro} setIsReplay={setIsReplay} />
        </div>
    );
};


export default StartGameVillages;
